import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import BasePageProps from '../../view2D/BasePageProps';
import BaseSection from '../../view2D/BaseSection';
import Image from '../../Image/Image';

import { useAudioContext } from '../../../context/AudioContext';
import { useDetailViewContext } from '../../../context/DetailViewContext';
import { DetailSectionIDs } from '../../../data/Sections';

import greenTick from '../../../assets/images/icons/green-tick.svg';
import styles from './styles.module.scss';
import useIsMobile from '../../../hooks/useIsMobile';

const Thumbnails = [
  {
    detailId: DetailSectionIDs.DecodingVector,
    position: 12,
    i18nId: 'vectorrepresentation.title_1',
    imageName: 'Vector-representation',
  },
  {
    detailId: DetailSectionIDs.DecodingSphere,
    position: 31,
    i18nId: 'sphereembedding.title_1',
    imageName: 'SphereEmbedding',
  },
  {
    detailId: DetailSectionIDs.DecodingSubspaces,
    position: 50,
    i18nId: 'spheresubspaces.title_1',
    imageName: 'SphereSubspaces',
  },
  {
    detailId: DetailSectionIDs.DecodingTopology,
    position: 69,
    i18nId: 'topologicalrelations.title_1',
    imageName: 'TopologicalRelations',
  },
  {
    detailId: DetailSectionIDs.DecodingDynamicBehaviour,
    position: 88,
    i18nId: 'dynamicbehavior.title_1',
    imageName: 'Dynamicbehaviour',
  },
];


function TheScience({ ...props }: BasePageProps): JSX.Element {
  const isMobile = useIsMobile();
  const { playAudio } = useAudioContext();
  const { activatedDetailIds, setDetailSectionID } = useDetailViewContext();

  const { t } = useTranslation('ai4Jokes');

  const isPrevDetailActivated = useCallback(
    (id: DetailSectionIDs) => {
      const index = Thumbnails.findIndex(t => t.detailId === id);
      if (index === -1)
        return false;
      if (index === 0)
        return true;
      return activatedDetailIds[Thumbnails[index - 1].detailId];
    },
    [activatedDetailIds]);


  const onThumbnailClick = useCallback((detailId) => {
    setDetailSectionID(detailId);
    playAudio('audioCTA');
  }, [setDetailSectionID, playAudio]);

  return <BaseSection {...props} style={{ position: 'fixed', height: '100%' }}>
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.upperTitle}>
          <div>
            {t('thescience.subheadline')}
          </div>
        </div>
        <div className={styles.title}>
          <h3>{t('thescience.headline-line-1')}</h3>
        </div>
        <div className={styles.title}>
          <h3>{t('thescience.headline-line-2')}</h3>
        </div>
        {
          t('thescience.headline-line-3') &&
          <div className={styles.title}>
            <h3>{t('thescience.headline-line-3')}
              {
                !isMobile && t('thescience.headline-line-4') && <> {t('thescience.headline-line-4')}</>
              }
            </h3>
          </div>
        }
        {
          t('thescience.headline-line-4')
          && isMobile
          &&
          <>
            {
              t('thescience.headline-line-4') &&
              <div className={styles.title}>
                <h3>{t('thescience.headline-line-4')}</h3>
              </div>
            }
          </>
        }

      </div>
      <div className={styles.centerImage}>
        <Image
          alt='last scene'
          filename='lastframe'
          imageConfig={{
            '(min-width: 600px)': {
              sizes: {
                '1x': 'md-1x',
                '2x': 'md-2x',
              },
              type: 'jpg'
            },
            '(max-width: 599px)': {
              sizes: {
                '1x': 'sm-1x',
                '2x': 'sm-2x',
              },
              type: 'jpg'
            }
          }} />
      </div>
      <div className={styles.bottomContainer}>
        <div>
          <div className={styles.progressBar} style={{ width: '100%' }}></div>
          <div className={styles.progressBarBg}>
            {Thumbnails.map((p, i) => {
              return isPrevDetailActivated(p.detailId) ? <button key={i}
                onClick={() => onThumbnailClick(p.detailId)}
                className={styles.thumbnailBig}
                style={{ left: p.position + '%', transform: `translateX(-${p.position}%)` }}>
                <div className={!activatedDetailIds[p.detailId] ? styles.bounce : ''}>
                  <div>
                    <Image
                      alt='snapshot from the video'
                      folder='thumbnails'
                      filename={p.imageName}
                    />
                    {activatedDetailIds[p.detailId] && <img src={greenTick} alt='green tick' className={styles.greenTick} />}
                    <div className={styles.title}>
                      <span>{t(p.i18nId)}</span>
                    </div>
                  </div>
                  <div></div>
                </div>
                <div className={styles.trail}></div>
              </button> : <div key={i}
                className={styles.thumbnailSmall}
                style={{ left: p.position + '%' }}>
                <div>
                  <Image
                    alt='snapshot from the video'
                    folder='thumbnails'
                    filename={p.imageName}
                  />
                  <div></div>
                </div>
                <div className={styles.trail}></div>
              </div>;
            })}
          </div>

        </div>
        <div className={styles.footer}>
          <div className={styles.subtitles}>
            {t('thescience.caption')}
          </div>
        </div>
      </div>
    </div>
  </BaseSection>;
}

export default React.memo(TheScience);